import { useState } from "react";
import { toast } from "sonner";
import { FileDown, FileSpreadsheet, Loader2 } from "lucide-react";
import { downloadFile, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const presetRange = (key) => {
  const now = new Date();
  const y = now.getFullYear();
  const q = Math.floor(now.getMonth() / 3);
  switch (key) {
    case "this_quarter": return [iso(new Date(y, q * 3, 1)), iso(now)];
    case "last_quarter": return [iso(new Date(y, q * 3 - 3, 1)), iso(new Date(y, q * 3, 0))];
    case "ytd": return [iso(new Date(y, 0, 1)), iso(now)];
    case "last_12": return [iso(new Date(y - 1, now.getMonth(), now.getDate() + 1)), iso(now)];
    case "last_year": return [iso(new Date(y - 1, 0, 1)), iso(new Date(y - 1, 11, 31))];
    default: return ["", ""];
  }
};

const PRESETS = [["this_quarter", "This quarter"], ["last_quarter", "Last quarter"], ["ytd", "Year to date"], ["last_12", "Last 12 months"], ["last_year", "Last calendar year"], ["all", "All records"], ["custom", "Custom dates"]];
const INPUT = "h-10 w-full rounded-md border bg-background px-3 text-sm";

export const FleetReportDialog = ({ onClose }) => {
  const [preset, setPreset] = useState("last_quarter");
  const [custom, setCustom] = useState(["", ""]);
  const [busy, setBusy] = useState(false);
  const [from, to] = preset === "custom" ? custom : presetRange(preset);
  const go = async (kind) => {
    if (from && to && from > to) return toast.error("Start date is after end date");
    setBusy(kind);
    const suffix = `${from ? `-${from}` : ""}${to ? `-to-${to}` : ""}`;
    const [path, name] = kind === "xlsx" ? ["/reports/fleet-costs.xlsx", `SSCBB-fleet-costs${suffix}.xlsx`] : ["/reports/fleet.pdf", `SSCBB-fleet-report${suffix}.pdf`];
    try {
      await downloadFile(path, name, { date_from: from || undefined, date_to: to || undefined });
      onClose();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="fleet-report-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Fleet report</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Costs, faults, repairs and rides are limited to the period. Bike status and compliance dates are always shown as they are today.</p>
        <div className="grid grid-cols-2 gap-2">
          {PRESETS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setPreset(k)} data-testid={`report-preset-${k}`}
              className={`rounded-full border-2 px-3 py-2 text-sm font-semibold ${preset === k ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
        {preset === "custom" ? (
          <div className="grid grid-cols-2 gap-3">
            <label className="space-y-1 text-xs text-muted-foreground">From<input type="date" value={custom[0]} onChange={(e) => setCustom([e.target.value, custom[1]])} className={INPUT} data-testid="fleet-report-from-input" /></label>
            <label className="space-y-1 text-xs text-muted-foreground">To<input type="date" value={custom[1]} onChange={(e) => setCustom([custom[0], e.target.value])} className={INPUT} data-testid="fleet-report-to-input" /></label>
          </div>
        ) : (
          <p data-testid="report-period-label" className="rounded-lg bg-secondary px-3 py-2 text-sm font-semibold">
            {from ? `${from.split("-").reverse().join("/")} to ${to.split("-").reverse().join("/")}` : "All records"}
          </p>
        )}
        <div className="grid gap-2 sm:grid-cols-2">
          <button onClick={() => go("pdf")} disabled={!!busy} data-testid="report-download-btn" className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary font-bold text-primary-foreground disabled:opacity-60">
            {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} Full report (PDF)
          </button>
          <button onClick={() => go("xlsx")} disabled={!!busy} data-testid="report-xlsx-btn" className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full border-2 border-emerald-500 font-bold text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-60">
            {busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Cost table (Excel)
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
